import type { ReflectionStep } from "../constants";
import type {
	ContributingFactor,
	Signal,
	CorrectiveAction,
	ReflectionTheme,
} from "../hooks/useReflectionState";
import type { ReflectionData as ReflectionDataService } from "../services/reflectionService";

interface BuildReflectionDataParams {
	title: string;
	description: string;
	futureState: string;
	qaHistory: Array<{ question: string; answer: string }>;
	currentQuestion: string;
	currentAnswer: string;
	conversationHistory: Array<{ role: string; content: string }>;
	selectedCategory: string | null;
	selectedTheme: ReflectionTheme | null;
	currentStep: ReflectionStep;
	contributingFactors: ContributingFactor[];
	signals: Signal[];
	correctiveActions: CorrectiveAction[];
	topicTags: string[];
}

/**
 * Build reflection data structure for saving
 */
export function buildReflectionData({
	title,
	description,
	futureState,
	qaHistory,
	currentQuestion,
	currentAnswer,
	conversationHistory,
	selectedCategory,
	selectedTheme,
	currentStep,
	contributingFactors,
	signals,
	correctiveActions,
	topicTags,
}: BuildReflectionDataParams): {
	reflectionData: {
		title: string;
		description: string;
		current_state: string;
		future_state: string;
		qa_results: any;
		topic_tags: string[];
	};
	serviceData: ReflectionDataService;
} {
	const reflectionData = {
		title: title || "Untitled Reflection",
		description: description || "",
		current_state: description || "",
		future_state: futureState,
		qa_results: {
			history: qaHistory,
			currentQuestion,
			currentAnswer,
			conversationHistory,
			selectedCategory,
			selectedTheme,
			currentStep,
			aiGeneratedFactors: contributingFactors.filter((f) => !f.selected),
			aiGeneratedSignals: signals.filter((s) => !s.selected),
			aiGeneratedActions: correctiveActions.filter((a) => !a.selected),
		},
		topic_tags: topicTags,
	};

	const serviceData: ReflectionDataService = {
		title: reflectionData.title,
		description: reflectionData.description,
		current_state: reflectionData.current_state,
		future_state: reflectionData.future_state,
		qa_results: reflectionData.qa_results,
		topic_tags: reflectionData.topic_tags,
		theme_id: selectedTheme?.id,
		is_draft: currentStep !== "summary",
	};

	return { reflectionData, serviceData };
}
